/** Typing @ in the QuantisAI composer offers the workspace's bots. Picking one
 * writes `@Name ` into the message; QuantisAI reads the mention and hands that
 * request to the bot. Only a mention at the end of the text is completed, so
 * the caret never needs tracking: that is where someone is typing it. */
import { useEffect, useMemo, useState, type KeyboardEvent } from 'react';
import { workspaceBotsService, type WorkspaceBot } from '../../services/WorkspaceBotsService';
import { BotAvatar } from '../workspaces/bots/BotAvatar';
import { resolveBotAppearance } from '../workspaces/bots/botAppearance';
import { cn } from '../../shared/ui/utils';

/** Reopening the panel must not refetch anything, so the list is kept per
 * workspace for a few minutes. A bot added meanwhile appears on the next load. */
const BOTS_TTL_MS = 5 * 60_000;
const botCache = new Map<string, { at: number; bots: WorkspaceBot[] }>();
const freshBots = (workspaceId: string | null | undefined) => {
  const cached = workspaceId ? botCache.get(workspaceId) : undefined;
  return cached && Date.now() - cached.at < BOTS_TTL_MS ? cached.bots : null;
};

const RETRY_MS = 30_000;
const MAX_RETRIES = 5;

/** The enabled bots of the chat's workspace; none until loaded. A failed load
 * is retried a few times, so a passing outage does not leave @ empty for as
 * long as the panel stays open. */
export function useWorkspaceBots(workspaceId: string | null | undefined): WorkspaceBot[] {
  const [loaded, setLoaded] = useState<{ workspaceId: string; bots: WorkspaceBot[] } | null>(null);
  const [attempt, setAttempt] = useState(0);
  useEffect(() => {
    if (!workspaceId || freshBots(workspaceId)) return;
    let active = true;
    let retry: ReturnType<typeof setTimeout> | undefined;
    workspaceBotsService.list(workspaceId).then((list) => {
      const enabled = list.filter((bot) => bot.isEnabled);
      botCache.set(workspaceId, { at: Date.now(), bots: enabled });
      if (active) setLoaded({ workspaceId, bots: enabled });
    }).catch(() => {
      if (active && attempt < MAX_RETRIES) retry = setTimeout(() => setAttempt((n) => n + 1), RETRY_MS);
    });
    return () => { active = false; if (retry) clearTimeout(retry); };
  }, [workspaceId, attempt]);
  if (!workspaceId) return noBots;
  return freshBots(workspaceId) ?? (loaded?.workspaceId === workspaceId ? loaded.bots : noBots);
}

const noBots: WorkspaceBot[] = [];
const TRAILING_MENTION = /(^|\s)@([^@\n]{0,40})$/;

/** QuantisAI's message limit; the composer cuts anything past it. */
const MESSAGE_MAX_LENGTH = 4000;

/** Exported for tests: the bots a trailing `@query` could complete to. A bot
 * whose completed name would pass the message limit is left out: the composer
 * would cut the mention short and it would name no bot. */
export function mentionMatches(value: string, bots: WorkspaceBot[]): { query: string; bots: WorkspaceBot[] } | null {
  const match = TRAILING_MENTION.exec(value);
  if (!match) return null;
  const query = match[2].toLocaleLowerCase();
  const room = MESSAGE_MAX_LENGTH - (value.length - match[2].length);
  const found = bots.filter((bot) => bot.isEnabled && bot.name.toLocaleLowerCase().startsWith(query) && bot.name.toLocaleLowerCase() !== query.trimEnd()
    && bot.name.length + 1 <= room);
  return found.length ? { query: match[2], bots: found.slice(0, 6) } : null;
}

export function useBotMention(value: string, onChange: (value: string) => void, bots: WorkspaceBot[]) {
  const [active, setActive] = useState(0);
  const [dismissed, setDismissed] = useState<string | null>(null);
  const matches = useMemo(() => (dismissed === value ? null : mentionMatches(value, bots)), [value, bots, dismissed]);
  const index = matches ? Math.min(active, matches.bots.length - 1) : 0;

  const pick = (bot: WorkspaceBot) => {
    if (!matches) return;
    onChange(`${value.slice(0, value.length - matches.query.length)}${bot.name} `);
    setActive(0);
  };
  /** Returns true when the key belonged to the menu. */
  const onKeyDown = (event: KeyboardEvent<HTMLTextAreaElement>): boolean => {
    if (!matches || event.nativeEvent.isComposing) return false;
    if (event.key === 'ArrowDown' || event.key === 'ArrowUp') {
      event.preventDefault();
      const step = event.key === 'ArrowDown' ? 1 : -1;
      setActive((index + step + matches.bots.length) % matches.bots.length);
      return true;
    }
    if ((event.key === 'Enter' && !event.shiftKey) || event.key === 'Tab') {
      event.preventDefault();
      pick(matches.bots[index]);
      return true;
    }
    if (event.key === 'Escape') {
      event.preventDefault();
      setDismissed(value);
      return true;
    }
    return false;
  };
  return { matches, index, pick, onKeyDown };
}

export function SIBotMentionMenu({ mention }: { mention: ReturnType<typeof useBotMention> }) {
  const { matches, index, pick } = mention;
  if (!matches) return null;
  return (
    <div role="listbox" aria-label="Workspace bots" className="mx-2 mb-1 overflow-hidden rounded-xl border border-border bg-popover py-1 shadow-md">
      {matches.bots.map((bot, i) => (
        <button key={bot.id} type="button" role="option" aria-selected={i === index}
          // Cancelling mousedown keeps focus in the composer so typing carries
          // on; the pick itself is on click, so Enter and Space work too.
          onMouseDown={(event) => event.preventDefault()}
          onClick={() => pick(bot)}
          className={cn('flex w-full items-center gap-2.5 px-3 py-1.5 text-left text-sm', i === index ? 'bg-accent text-accent-foreground' : 'hover:bg-muted')}>
          <BotAvatar appearance={resolveBotAppearance(bot)} size={24} />
          <span className="min-w-0 truncate font-medium">{bot.name}</span>
          <span className="ml-auto shrink-0 text-xs text-muted-foreground">Bot</span>
        </button>
      ))}
    </div>
  );
}
